import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEmpty } from 'lodash-es';

import { Events } from '@omni/events';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DiskService } from '@omni/services/disk/disk.service';
import { AgendaFooterService } from '@omni/services/footer/agenda-footer.service';
import { DynamicForm, FormType } from '@omni/classes/dynamic-form/dynamic-form.class';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { DynamicFormComponentModel, DynamicFormType } from '@omni/models/dynamic-form-component.model';
import { AlertService } from '@omni/services/alert/alert.service';
import { SetBookingActivity, SetBookingStatus } from '@omni/classes/activity/set-booking.activity.class';
import { SetBookingDataService } from '@omni/data-services/set-booking/set-booking.data.service';

import { OperationDetail } from '../../data-services/order-activity/order-activity.data.service';
import { ActivityService } from '../activity/activity.service';
import { AuthenticationService } from '../authentication.service';
import { ContactOfflineService } from '../contact/contact.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { FooterService, FooterViews } from '../footer/footer.service';
import { TrackService, TrackingEventNames } from '../logging/tracking.service';
import { ChildNavNames, NavigationService, PageName } from '../navigation/navigation.service';
import { NotificationService, ToastStyle } from '../notification/notification.service';
import { UIService } from '../ui/ui.service';

export interface KitBookingCreateContext {
  /** 'AccountTool' | 'ContactPageComponent' when opened from a tool's layover, else absent. */
  layoverPushedFrom?: string;
  layoverPassedData?: any;
  /**
   * Push the form onto the current tool's right pane and put that pane back when the form
   * closes, instead of rooting it in Activities. Used by the top bar, where the rep should
   * stay where they are. Requires masterPage.
   */
  stayInTool?: boolean;
  masterPage?: PageName;
  /**
   * False when the caller was not pushed by New Activity. The layover branches pop before
   * pushing, because New Activity put itself on the stack and the form takes its place;
   * a surface calling directly has nothing to remove and would pop itself.
   */
  replacesNewActivity?: boolean;
}

/**
 * Tools that keep their right-pane nav hidden until uiService.activeView names it.
 * ponytail: duplicated from ActivityCreationService — merge when a third surface needs it.
 */
const ACTIVE_VIEW_GATED_TOOLS = new Map<PageName, string>([
  [PageName.AllocationComponent, ChildNavNames.CustomerAllocationsToolNavigation],
  [PageName.CaseManagementHomePage, ChildNavNames.CaseManagementNavigation],
  [PageName.TimeOffComponent, ChildNavNames.TimeOffNavigation],
]);

/**
 * Kit Booking creation, lifted out of NewActivityComponent so more than one surface can
 * open the form. Moved verbatim apart from the two deviations noted inline.
 */
@Injectable({ providedIn: 'root' })
export class KitBookingFormService {

  private linkEntitiesReqPayload: any = [];
  /** Set only for a stay-in-tool open; what the host tool's right pane showed before. */
  private _hostRestore: { activeView: string; showRightPane: boolean; childNavRightPane: boolean } | null = null;

  constructor(
    public readonly authService: AuthenticationService,
    public uiService: UIService,
    private contactService: ContactOfflineService,
    public activityOfflineService: ActivityService,
    private events: Events,
    private footerService: FooterService,
    private trackingService: TrackService,
    private navService: NavigationService,
    public translate: TranslateService,
    public dateTimeFormatsService: DateTimeFormatsService,
    private notificationService: NotificationService,
    private agendaFooterService: AgendaFooterService,
    private readonly dynamicFormService: DynamicFormsService,
    private readonly alertService: AlertService,
    private readonly setBookingDataService: SetBookingDataService,
    private readonly diskService: DiskService,
  ) { }

  /**
   * Deviation from the component original, which compared getActiveChildComponent() against
   * the NewActivityComponent class — importing that here would be circular. The tracked page
   * name is the same test without the import.
   */
  private async _pushChildNavPage(pageComponentToPush: any, newPageName: PageName, masterPageName?: PageName, params?: any, options?: any) {
    if (this.navService.getActiveChildNavViewPageName() === PageName.NewActivityComponent
      && !await this.navService.canChildNavGoBack()) {
      await this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, masterPageName, params);
    }
    return this.navService.pushChildNavPageWithPageTracking(pageComponentToPush, newPageName, masterPageName, params, options);
  }

  /**
   * Opens the Kit Booking create form. The record is written when the rep saves, not here
   * — the eventsHandler below owns submission, validation and cancellation.
   */
  public async openCreateForm(context: KitBookingCreateContext = {}): Promise<void> {
    // Per-form state. The component instance used to be new on every push; this service
    // is not, so without the reset the next booking inherits the last one's linked entities.
    this.linkEntitiesReqPayload = [];
    let kitBookingDynamicForm: DynamicForm = this.dynamicFormService.getFormDefinitionForEntity('indskr_setbooking',FormType.CREATEFORM);
    const nonVisibleFormSection = this.setBookingDataService.formSectionConfigure?.filter(config => config['_indskr_mobileappform_value'] == kitBookingDynamicForm?.mobileFormId && !config['indskr_visibility']);
    kitBookingDynamicForm?.metadata.forEach(tab => {
      tab.controls.forEach(control => {
        if (control.dataType && control.attributeName && !isEmpty(nonVisibleFormSection) && nonVisibleFormSection.some(sec => sec['indskr_name'].toLowerCase() == control.attributeName.toLowerCase())) {
          control.isVisible = false;
          control.forceHide = true;
        } else if (!control.dataType && control.subgrid && !isEmpty(nonVisibleFormSection) && nonVisibleFormSection.some(sec => sec['indskr_name'].toLowerCase() == control.attributeName.toLowerCase())) {
          control.isVisible = false;
          control.forceHide = true;
        }
      });
    });
    const formType = DynamicFormType.CONFIGUREDFORM;
    if (kitBookingDynamicForm) {
      let formDetail: DynamicFormComponentModel = {
        title: this.translate.instant('KIT_BOOKING'),
        LookupEntitySetName: 'indskr_setbookings',
        showLeftHeaderButton: true,
        leftHeaderBtnIcon: 'chevron-back-outline',
        showRightHeaderButton: true,
        rightHeaderImgSrc: 'assets/imgs/header_save.svg',
        rightHeaderBtnText: this.translate.instant('SAVE'),
        formMetadata: kitBookingDynamicForm,
        formEntityObject: null,
        mainEntityObjectId: null,
        dynamicFormType: formType,
        navOptions: { animate: false },
        eventsHandler: (data: any, eventTarget: string, refData: DynamicFormComponentModel) => this._handleKitBookingFormComponentEvent(data, eventTarget, refData),
      };
      if(context.layoverPushedFrom){
        let from ="";
        formDetail.formEntityObject = {};
        let pageName;
        if(context.layoverPushedFrom == "AccountTool"){
          formDetail.formEntityObject['_indskr_accountnamesoldto_value'] = context.layoverPassedData?.id;
          formDetail.formEntityObject['_indskr_accountnamesoldto_value@OData.Community.Display.V1.FormattedValue'] = context.layoverPassedData?.accountName;
          from = "KitBookingfromAccountTool";
          pageName = PageName.AccountPageComponent;
          this.uiService.activeView = 'AccountPageRightPaneNav';
        }
        if(context.layoverPushedFrom == "ContactPageComponent"){
          formDetail.formEntityObject['_indskr_surgeoncontact_value'] = this.contactService.contactInformation?.ID;
          formDetail.formEntityObject['_indskr_surgeoncontact_value@OData.Community.Display.V1.FormattedValue'] = this.contactService.contactInformation?.fullname;
          from = "KitBookingfromContactTool";
          pageName = PageName.ContactPageComponent;
          this.uiService.activeView = 'ContactPageRightPaneNav';
        }
        this.uiService.showNewActivity = false;
        if (context.replacesNewActivity !== false) await this.navService.popChildNavPageWithPageTracking();
        const formPushed = this._pushChildNavPage(PageName.DynamicFormComponent, PageName.DynamicFormComponent, pageName, {
          MODE: 'CREATE_MODE',
          FORM_DETAIL: formDetail,
          from: from,
        });
        this.footerService.initButtons(FooterViews.Accounts);
        this.activityOfflineService.finalizeAgendaRefresh();
        // Awaited last, so the footer keeps its old timing but callers still wait for the push.
        await formPushed;
      }
      else if (context.stayInTool) {
        this._hostRestore = {
          activeView: this.uiService.activeView,
          showRightPane: this.uiService.showRightPane,
          childNavRightPane: !!this.navService.isActiveChildNavRightPaneViewDisplayed,
        };
        const gatedActiveView = ACTIVE_VIEW_GATED_TOOLS.get(context.masterPage) ?? null;
        if (gatedActiveView) this.uiService.activeView = gatedActiveView;
        this.uiService.showRightPane = true;
        this.navService.setChildNavRightPaneView(true);
        this.uiService.showNewActivity = false;
        this.activityOfflineService.selected = null;
        this.footerService.initButtons(FooterViews.Accounts);
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.DynamicFormComponent, PageName.DynamicFormComponent, context.masterPage, {
            MODE: 'CREATE_MODE',
            FORM_DETAIL: formDetail,
          });
      }
      else{
        const formShown = this.navService.setChildNavRoot(PageName.DynamicFormComponent, PageName.DynamicFormComponent, PageName.GoAppActivitiesPageComponent, {
          MODE: 'CREATE_MODE',
          FORM_DETAIL: formDetail,
        });
        this.footerService.initButtons(FooterViews.Accounts);
        this.navService.setChildNavRightPaneView(true);
        this.uiService.showNewActivity = false;
        this.activityOfflineService.selected = null;
        await formShown;
      }

    }
  }

  private async _validateLimitedReleaseProducts(data: any, eventTarget: string): Promise<boolean> {
    if(eventTarget != 'SUBMITKITBOOKING' || !data.formValue["indskr_setbookingid"]) {
      return true;
    }
    const selectedProductIds = await this._getSelectedProductIds(data);
    if(!selectedProductIds?.length) return true;

    const productLimitedRelease = await this.setBookingDataService.getLimitedReleaseInfoForSelectedProduct(selectedProductIds);
    if(!productLimitedRelease?.length) return true;

    const limitedReleaseProducts = productLimitedRelease.filter(pr => pr.indskr_limitedrelease);
    if(limitedReleaseProducts.length > 0) {
      return await this._validatePHMCAccount(data);
    }
    
    return true;
  } 

  private async _getSelectedProductIds(data: any): Promise<string[]> {
    const selectedProductIds: string[] = [];

    if(this.linkEntitiesReqPayload?.length) {
      this.linkEntitiesReqPayload.forEach(payload => {
        if(payload.values?.lookupfields?.length) {
          payload.values.lookupfields.forEach((field: any) => {
            if(field.name === "indskr_product") {
              selectedProductIds.push(field.id);
            }
          });
        }
      });
    } else {
      const setBookingProductsArray = await this.diskService.retrieve(DB_KEY_PREFIXES.SET_BOOKING_LINKED_ENTITY + "indskr_setbookingproduct");
      const linkEntityProductsData = setBookingProductsArray?.raw?.filter(x => 
        x["indskr_setbookingid"] === data.formValue["indskr_setbookingid"] && 
        (!x.hasOwnProperty('indskr_setbookingproduct.statecode') || x['indskr_setbookingproduct.statecode'] == 0)
      );
      
      if(linkEntityProductsData?.length) {
        linkEntityProductsData.forEach(pro => {
          selectedProductIds.push(pro["indskr_setbookingproduct.indskr_product"]);
        });
      }
    }

    return selectedProductIds;
  }



  private async _validatePHMCAccount(data: any): Promise<boolean> {
    const phmcIndex = this._findPHMCAccountIndex(data.formValue);
    
    if(phmcIndex >= 0 && this.authService.user.buSettings["indskr_limitedreleasebookingmessage"]) {
      const res = await this.alertService.showAlert({
        header: this.translate.instant('NOTE'),
        message: this.authService.user.buSettings["indskr_limitedreleasebookingmessage"],
        backdropDismiss: false,
      }, this.translate.instant('OK'));
      
      if(res?.role === "cancel") return false;
    } else if(phmcIndex < 0) {
      this.notificationService.notify(this.translate.instant('KIT_BOOKING_PHMC_ERROR_MSG'), 'New Activity', 'top', ToastStyle.DANGER);
      return false;
    }

    return true;
  }

  private _findPHMCAccountIndex(formValue: any): number {
    if(!formValue.hasOwnProperty('lookupfields')) return -1;
    
    return formValue['lookupfields'].findIndex((a: any) => 
      a.name === "indskr_PHMCAccount" && 
      !(a['isDeleted'] && a['isDeleted'] === true) && 
      a.id
    );
  }

  private _handleKitBookingSuccess(newSetBooking: SetBookingActivity, data: any, eventTarget: string, res: any) {
    if (this.uiService.activitiesPageTab == 'day' || this.activityOfflineService.newHomeView === 'calendarView') {
      let uiData = {
        id: newSetBooking.ID,
        subject: newSetBooking.subject,
        location: newSetBooking.statusString,
        scheduledStart: newSetBooking.scheduledStart,
        scheduledEnd: newSetBooking.scheduledEnd,
        type: newSetBooking.type,
      };
      this.uiService.setUIServiceData({
        view: 'new-activity', data: uiData,
      });
    }
    this.activityOfflineService.finalizeAgendaRefresh();
    if (this.agendaFooterService.actSegment === 'week') {
      this.events.publish('weekview:RefreshUI');
    }
    
    if(!data || !data.eventsHandler) {
      return;
    }
    
    let rawDetails = { indskr_setbookingid: newSetBooking.ID };
    this.activityOfflineService.selected = newSetBooking;
    this.dynamicFormService.linkedEntityFormIsDirtyTracker = false;
    this.setBookingDataService._isCreateEditKitBookingFormDirty = false;
    
    if (data.isFormSaved) {
      this._handleFormSavedNavigation(data, eventTarget, newSetBooking);
      data.eventsHandler(rawDetails, 'NEWDATAUPDATED');
      if(eventTarget == "SUBMITKITBOOKING") {
        this.notificationService.notify(this.translate.instant('KIT_BOOKING_USER_SUBMITTED'), "ContactInfoComponent", "top", ToastStyle.KITBOOKING_INFO, 3000);
      } else {
        this.notificationService.notify(this.translate.instant('UPDATED_SUCCESS'), "ContactInfoComponent", "top", ToastStyle.INFO, 3000);
      }
    } else {
      data.eventsHandler(rawDetails, 'NEWDATACREATED');
      this.notificationService.notify(this.translate.instant('KIT_BOOKING_NEW_CREATED'), "DynamicFormComponent", "top", ToastStyle.INFO, 3000);
    }
  } 

  private _handleFormSavedNavigation(data: any, eventTarget: string, newSetBooking: SetBookingActivity) {
    if(data.layoverViewPushedFrom && (data.layoverViewPushedFrom == 'KitBookingfromAccountTool' || data.layoverViewPushedFrom == 'KitBookingfromContactTool')){
      if(eventTarget == 'SUBMITKITBOOKING'){
        if(data.layoverViewPushedFrom == 'KitBookingfromAccountTool'){
          this.uiService.activeView = 'accountDetails';
          this.footerService.initButtons(FooterViews.Accounts);
          this.navService.popChildNavPageWithPageTracking();
        } else if(data.layoverViewPushedFrom == 'KitBookingfromContactTool'){
          this.uiService.activeView = 'contactDetails';
          this.footerService.initButtons(FooterViews.Contacts);
          this.navService.popChildNavPageWithPageTracking();
        }
      }
    } else if (this._releaseHostPane()) {
      // Stayed in the host tool, so there is no Activities pane to select the record in.
    } else {
      this.events.publish('activity-pane:selectActivity-kitBookingSubmission', newSetBooking);
    }
  }

  /** Pops a stay-in-tool form and restores the host tool's pane. False when not hosted. */
  private _releaseHostPane(): boolean {
    if (!this._hostRestore) return false;
    const restore = this._hostRestore;
    this._hostRestore = null;
    this.uiService.activeView = restore.activeView;
    this.uiService.showRightPane = restore.showRightPane;
    this.navService.setChildNavRightPaneView(restore.childNavRightPane);
    this.navService.popChildNavPageWithPageTracking();
    return true;
  }

  private _handleCancelNavigation(data: any) {
    if(data.layoverViewPushedFrom && data.layoverViewPushedFrom == 'KitBookingfromAccountTool'){
      this.uiService.activeView = 'accountDetails';
      this.footerService.initButtons(FooterViews.Accounts);
      this.navService.popChildNavPageWithPageTracking();
    } else if(data.layoverViewPushedFrom && data.layoverViewPushedFrom == 'KitBookingfromContactTool'){
      this.uiService.activeView = 'contactDetails';
      this.footerService.initButtons(FooterViews.Contacts);
      this.navService.popChildNavPageWithPageTracking();
    } else if (this._releaseHostPane()) {
      // Handled: popped back to whatever the tool had open before the form.
    } else {
      this.navService.popToRootChildNavPageWithPageTracking();
      this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.GoAppActivitiesPageComponent);
      this.uiService.showRightPane = false;
      this.navService.setChildNavRightPaneView(false);
    }
  }
  
  private async _handleKitBookingFormComponentEvent(data, eventTarget, refData: DynamicFormComponentModel) {
    if (!eventTarget) return;

    switch (eventTarget) {
      case 'DONEBUTTONCLICK':
      case 'SUBMITKITBOOKING':
      case 'SAVEKITBOOKING':
        await this._handleKitBookingSubmission(data, eventTarget);
        break;
      case 'CANCELBUTTONCLICK':
        await this._handleKitBookingCancellation(data);
        break;
      case 'ADDEDLINKEDENTITY':
      case 'SCRAPLINKEDENTITY':
        await this._handleLinkedEntityEvent(data);
        break;
      case 'ISFORMDIRTY':
        this._handleFormDirtyEvent(data);
        break;
    }
  }

  private async _handleKitBookingSubmission(data: any, eventTarget: string) {
    if (!data?.formValue) return;

    if (eventTarget === 'SUBMITKITBOOKING') {
      this.trackingService.tracking('KitBookingSubmitClick', TrackingEventNames.KITBOOKING);
    }

    if (!(await this._validateLimitedReleaseProducts(data, eventTarget))) {
      return;
    }

    if (!(await this._validateUrgentKitBooking(data, eventTarget))) {
      return;
    }

    if (!(await this._validateLateKitBooking(data, eventTarget))) {
      return;
    }

    if (!(await this._validateStartDate(data, eventTarget))) {
      return;
    }

    await this._processKitBookingSubmission(data, eventTarget);
  }

  private async _validateUrgentKitBooking(data: any, eventTarget: string): Promise<boolean> {
    if (eventTarget === "SUBMITKITBOOKING" && 
        this.authService.user.buSettings["indskr_urgentkitbookingmessage"] && 
        data.formValue["indskr_urgent"]) {
      await this.alertService.showAlert({
        header: this.translate.instant('NOTE'),
        message: this.authService.user.buSettings["indskr_urgentkitbookingmessage"],
        backdropDismiss: false,
        buttons: [{ text: this.translate.instant('OK'), role: "ok" }]
      });
    }
    
    return true;
  }

  private async _validateLateKitBooking(data: any, eventTarget: string): Promise<boolean> {
    if (eventTarget !== 'SUBMITKITBOOKING' ||
        !this._isLateKitBookingConfigured() ||
        !this.authService.user.buSettings["indskr_latekitbookingmessage"]) {
      return true;
    }

    const daysDiff = this.dateTimeFormatsService.getDifferenceInCalendarDays(data.formValue["indskr_startdate"], new Date());
    const maxDays = this.authService.user.buSettings["indskr_latekitbookingdays"];
    
    if (daysDiff < 0 || daysDiff > maxDays) {
      return true;
    }

    const res = await this.alertService.showAlert({
      header: `Booking required within ${maxDays} days`,
      message: this.authService.user.buSettings["indskr_latekitbookingmessage"],
      backdropDismiss: false
    }, this.translate.instant('SUBMIT'));

    return !(res?.role === "cancel");
  }

  private _isLateKitBookingConfigured(): boolean {
    const setting = this.authService.user.buSettings["indskr_latekitbookingdays"];
    return setting !== undefined && setting !== null;
  }

  private async _validateStartDate(data: any, eventTarget: string): Promise<boolean> {
    if (eventTarget !== 'SUBMITKITBOOKING') {
      return true;
    }

    const daysDiff = this.dateTimeFormatsService.getDifferenceInCalendarDays(data.formValue["indskr_startdate"], new Date());
    const diffInMin = this.dateTimeFormatsService.getDifferenceInMinutes(new Date(data.formValue["indskr_startdate"]), new Date());
    
    if (daysDiff < 0 || (daysDiff === 0 && diffInMin < 0)) {
      this.notificationService.notify(
        this.translate.instant('DATE_PICKER_START_DATE_CAN_NOT_BEFORE_CURRENT_DATE', { kitbookingaction: "submitted" }), 
        'New Activity', 
        'top', 
        ToastStyle.DANGER
      );
      return false;
    }
    return true;
  }

  private async _processKitBookingSubmission(data: any, eventTarget: string) {
    this.uiService.displayLoader();
    
    await this._processLinkedEntities();
    const newSetBooking = await this._createSetBookingActivity(data, eventTarget);
    await this._saveSetBookingActivity(newSetBooking, data, eventTarget);
    
    this.uiService.dismissLoader();
  }

  private async _processLinkedEntities() {
    const taskScrap = this._getScrapTasks();
    await Promise.all(taskScrap).then(() => {}).catch(() => {});
    
    const tasks = this._getCreateTasks();
    await Promise.all(tasks);
  }

  private _getScrapTasks(): Promise<any>[] {
    if (!this.linkEntitiesReqPayload?.length) return [];
    
    return this.linkEntitiesReqPayload
      .filter(reqPayload => reqPayload.values?.statecode === 1)
      .map(reqPayload => this.dynamicFormService.createLinkedEntity(reqPayload.values, reqPayload.entity));
  }

  private _getCreateTasks(): Promise<any>[] {
    if (!this.linkEntitiesReqPayload?.length) return [];
    
    const tasks = this.linkEntitiesReqPayload
      .filter(reqPayload => reqPayload.values?.statecode !== 1)
      .map(reqPayload => this.dynamicFormService.createLinkedEntity(reqPayload.values, reqPayload.entity));
    
    this.linkEntitiesReqPayload = [];
    return tasks;
  }

  private async _createSetBookingActivity(data: any, eventTarget: string): Promise<SetBookingActivity> {
    const newSetBooking = new SetBookingActivity(data.formValue);
    newSetBooking.allDayEvent = !this.dateTimeFormatsService.isSameDayCheck(newSetBooking.scheduledStart, newSetBooking.scheduledEnd);
    newSetBooking.serviceDTO = cloneDeep(data.formValue);
    
    this._removeDefaultCaseTeamIfUnchanged(newSetBooking, data);
    this._mapDateFields(newSetBooking);
    this._setBookingStatus(newSetBooking, data, eventTarget);
    
    return newSetBooking;
  }

  private _removeDefaultCaseTeamIfUnchanged(newSetBooking: SetBookingActivity, data: any) {
    const selectedActivity = this.activityOfflineService.getActivityByID(newSetBooking.ID) as SetBookingActivity;
    if (selectedActivity?.rawData?.['indskr_defaultcaseteam'] !== undefined && 
        selectedActivity.rawData['indskr_defaultcaseteam'] === data.formValue['indskr_defaultcaseteam']) {
      delete newSetBooking.serviceDTO['indskr_defaultcaseteam'];
    }
  }

  private _mapDateFields(newSetBooking: SetBookingActivity) {
    if (newSetBooking.serviceDTO['indskr_startdate']) {
      newSetBooking.serviceDTO['indskr_operationstartdate'] = newSetBooking.serviceDTO['indskr_startdate'];
      delete newSetBooking.serviceDTO.indskr_startdate;
    }
    if (newSetBooking.serviceDTO['indskr_enddate']) {
      newSetBooking.serviceDTO['indskr_operationenddate'] = newSetBooking.serviceDTO['indskr_enddate'];
      delete newSetBooking.serviceDTO.indskr_enddate;
    }
  }

  private _setBookingStatus(newSetBooking: SetBookingActivity, data: any, eventTarget: string) {
    if (data.isFormSaved && eventTarget === 'SUBMITKITBOOKING') {
      newSetBooking.serviceDTO['indskr_status'] = SetBookingStatus.PENDINGSYNC;
      newSetBooking.statusString = 'Pending Sync';
    } else {
      newSetBooking.serviceDTO['indskr_status'] = SetBookingStatus.DRAFT;
      newSetBooking.serviceDTO['indskr_source'] = 548910001;
      newSetBooking.statusString = 'Draft';
    }
  }

  private async _saveSetBookingActivity(newSetBooking: SetBookingActivity, data: any, eventTarget: string) {
    const action: OperationDetail = {
      onDynamics: true,
      onLocalDatabase: true,
      onLocalCopy: true,
      operationDetail: {
        code: 'CREATENEWSETBOOKINGACTIVITY',
        message: 'Create New Set Booking Activity'
      }
    };

    const tasks = [this.setBookingDataService.createSetBookingActivity(action, [newSetBooking], new Date().getTime())];
    
    Promise.all(tasks).then(async (res) => {
      if (!res) {
        this.uiService.dismissLoader();
        return;
      }
      await this.setBookingDataService.fetchRealTimeSetBookingDetails(newSetBooking);
      this._handleKitBookingSuccess(newSetBooking, data, eventTarget, res);
      this.uiService.dismissLoader();
    }).catch(() => {
      this.uiService.dismissLoader();
    });
  }

  private async _handleKitBookingCancellation(data: any) {
    if (data?.displayConfirmation) {
      const flag = await this.setBookingDataService.checkSetBookingDiscardConfirmationRequired().catch(err => {});
      if (flag) {
        this._resetFormState();
        this._handleCancelNavigation(data);
      }
    } else {
      this._handleCancelNavigation(data);
    }
  }

  private _resetFormState() {
    this.dynamicFormService.linkedEntityFormIsDirtyTracker = false;
    this.dynamicFormService.okAddressMapper = {};
    this.dynamicFormService.okLookUpMapper = [];
    this.linkEntitiesReqPayload = [];
  }

  private async _handleLinkedEntityEvent(data: any) {
    this.trackingService.tracking('DoneSelected', TrackingEventNames.ACCOUNT_EDIT, true);
    if (data?.entity && data?.values) {
      this.addLinkEntitiesForKitBooking(data);
    }
  }

  private _handleFormDirtyEvent(data: any) {
    this.setBookingDataService._isCreateEditKitBookingFormDirty = data;
  }

  private addLinkEntitiesForKitBooking(linkEntity) {
    if (linkEntity && linkEntity['entity'] == 'indskr_setbookingproducts' && linkEntity['linkedEntityAddedNewDataId']
        && linkEntity['values'] && linkEntity['values']['statecode'] == 1
        && !isEmpty(this.linkEntitiesReqPayload)) {
        let idx = this.linkEntitiesReqPayload.findIndex(le => le.addedNewDataId == linkEntity['linkedEntityAddedNewDataId']);
        if(idx>-1) {
          this.linkEntitiesReqPayload.splice(idx,1);
      }
    } else if (isEmpty(linkEntity))
      this.linkEntitiesReqPayload = [];
    else {
      const linkEntityPayload = {
        "values": linkEntity["values"],
        "entity": linkEntity["entity"],
        "linkedEntityId": linkEntity["linkedEntityExistingDataId"],
        "addedNewDataId": linkEntity["linkedEntityAddedNewDataId"]
      }
      if (!isEmpty(this.linkEntitiesReqPayload)) {
        let index;
          if (linkEntity["linkedEntityExistingDataId"]) {
            index = this.linkEntitiesReqPayload.findIndex(x => x.linkedEntityId == linkEntity["linkedEntityExistingDataId"]);
          } else {
            index = this.linkEntitiesReqPayload.findIndex(x => x.addedNewDataId == linkEntity["linkedEntityAddedNewDataId"]);
          }
          if (index != -1) {
            if(linkEntity["linkedEntityAddedNewDataId"] && !linkEntity["linkedEntityExistingDataId"] && linkEntityPayload.values.statecode == 1) this.linkEntitiesReqPayload.splice(index, 1);
            else this.linkEntitiesReqPayload[index] = linkEntityPayload;
          } else this.linkEntitiesReqPayload.push(linkEntityPayload);
        } else {
          this.linkEntitiesReqPayload.push(linkEntityPayload);
        }
    }
  }
}
